import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  BackCushionTemplate,
  CreateBackCushionTemplate,
  UpdateBackCushionTemplate,
} from '@estimator/shared';

export function useBackCushionTemplates() {
  return useQuery({
    queryKey: ['back-cushion-templates'],
    queryFn: async () => {
      const { data } = await apiClient.get<BackCushionTemplate[]>('/banquette-back-cushion-templates');
      return data;
    },
  });
}

export function useBackCushionTemplate(id: string | null) {
  return useQuery({
    queryKey: ['back-cushion-templates', id],
    queryFn: async () => {
      const { data } = await apiClient.get<BackCushionTemplate>(
        `/banquette-back-cushion-templates/${id}`,
      );
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateBackCushionTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBackCushionTemplate) => {
      const { data } = await apiClient.post<BackCushionTemplate>(
        '/banquette-back-cushion-templates',
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-cushion-templates'] });
    },
  });
}

export function useUpdateBackCushionTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateBackCushionTemplate & { id: string }) => {
      const { data } = await apiClient.patch<BackCushionTemplate>(
        `/banquette-back-cushion-templates/${id}`,
        body,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['back-cushion-templates'] });
      queryClient.invalidateQueries({ queryKey: ['back-cushion-templates', vars.id] });
    },
  });
}

export function useDeleteBackCushionTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/banquette-back-cushion-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['back-cushion-templates'] });
    },
  });
}
